import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { SerializableMemberHistory, nFitHistoryRecord } from '../../interfaces';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { replaceMemberHistory } from '../../features/coach-members/coach-member-slice';
import { getCoachMemberHistory } from '../../services/resource-server/resource-server';
import { convertDateForHistory } from '../../utilities/display-formats';

import { memberColors } from './chart-colors';

const lineColors = memberColors;

const StripedBgPlugin = {
    id: 'StripedBg',
    beforeDraw: function (chart: any, args: any, options: any) {
        if (!chart.scales?.y?.ticks) return;
        const tickArray = chart.scales.y.ticks;
        const minTick = tickArray.reduce((accumulator: number, currentValue: any) => Math.min(accumulator, currentValue.value), 999);
        const maxTick = tickArray.reduce((accumulator: number, currentValue: any) => Math.max(accumulator, currentValue.value), -999);

        const fiveSectionRange: number = maxTick - minTick;

        const ctx = chart.ctx;
        const chartArea = chart.chartArea;

        const width = chartArea.right - chartArea.left;
        const height = chartArea.bottom - chartArea.top;

        // reset to white on "redraw"
        ctx.fillStyle = "rgba(255, 255, 255, 1.0)";
        ctx.fillRect(0, 0, width, height);

        const topMargin = chartArea.top;
        const leftMargin = chartArea.left;

        const colors = ['0C8140', '75A377', 'F2F2F4', '75ACD4', '1376BC'];
        const colorStart = [100, 1.5, 0.5, -0.5, -1.5];
        const colorEnd = [1.5, 0.5, -0.5, -1.5, -100];

        let edge = 0;
        let filledSoFar = 0;
        for (let i = 0; i < colorStart.length; i++) {
            if (colorStart[i] >= minTick) {
                let chunk = 0;
                if (i === 0 && maxTick > colorEnd[i]) {
                    if (minTick > colorEnd[i]) {
                        chunk = maxTick - minTick;
                    } else {
                        chunk = maxTick - colorEnd[i];
                    }
                } else if (i === colors.length - 1 && minTick > colorEnd[i]) {
                    chunk = colorStart[i] - minTick;
                } else {
                    chunk = colorStart[i] - colorEnd[i];
                }
                let percent: number = chunk / parseFloat(fiveSectionRange.toFixed(20));
                if (percent > 1)
                    percent = 1;

                let sectionHeight = Math.floor(height * percent);

                if ((filledSoFar + sectionHeight) > height) {
                    sectionHeight = height - filledSoFar;
                }

                edge = Math.floor(filledSoFar + (sectionHeight * 0.5));
                filledSoFar += sectionHeight;

                let extra = 0;
                if (i === colors.length - 1 && filledSoFar <= height) {
                    extra = height - filledSoFar;
                }

                ctx.beginPath();
                ctx.strokeStyle = '#' + colors[i];
                ctx.lineWidth = sectionHeight + extra;
                ctx.moveTo(leftMargin + 0, edge + topMargin);
                ctx.lineTo(leftMargin + width, edge + topMargin);
                ctx.stroke();
            }
        }
    },
};

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    StripedBgPlugin
);

interface StructuredData {
    labels: string[];
    datasets: any[];
}

interface ChartProps {
    start: Date,
}

interface DataToChart {
    label: string;
    colorIndex: number;
    history: nFitHistoryRecord[];
}

function NFitGroupHistoryChart(props: ChartProps) {
    const coachMembers = useAppSelector((state) => state.coachMembers.members);
    const coachedUsernames = useAppSelector((state) => state.coachMembers.usernames);
    const dispatch = useAppDispatch();

    const { start } = props;

    const chartData = useAppSelector((state) => state.scanResults.chartIds);
    const graphedHistory = useAppSelector((state) => state.scanResults.history);
    let [data, setData] = useState<StructuredData>({ labels: [], datasets: [] });

    /*
    const setScanResult = (record: nFitHistoryRecord) => {
        dispatch(selectScanResult({
            id: record.id,
            date: record.date.toLocaleDateString(),
            nFit: parseFloat(record.nFit.toPrecision(4)),
            annotation: record.annotation || '',
        }));
    };
    */

    const options = {
        elements: {
            line: {
                borderWidth: 2
            }
        },
        onClick: function (evt: any, element: any) {
            // if a data point on the graph is selected, save it to state
            if (element.length > 0) {
                const record_id = chartData[element[0].index];
                const record = graphedHistory.find(h => h.id === record_id);
                if (record) {
                    /*
                    const annotatedString = record.annotation;
                    setScanResult({
                        id: record.id,
                        date: new Date(record.date),
                        nFit: parseFloat(record.nFit.toPrecision(4)),
                        annotation: annotatedString,
                    });
                    */
                }
            }
        },
        plugins:
        {
            legend: {
                display: true,
                position: 'top' as const,
                labels: {
                    usePointStyle: true,
                    filter: (item: any) => !item.text.includes('(14d avg)'),
                },
            },
            title: {
                display: false,
                text: 'nFit Score History',
            }
        },
        responsive: true,
        scales: {
            x: {
                afterBuildTicks: (axis: any) => {
                    axis.ticks = axis.ticks.filter((tick: any) => {
                        const label = axis.getLabelForValue(tick.value);
                        return label && label.length > 0;
                    });
                },
                ticks: {
                    font: {
                        family: "Inter, sans-serif",
                        size: 10
                    },
                    autoSkip: true,
                    autoSkipPadding: 20,
                    maxRotation: 0,
                }
            },
            y: {
                grid: {
                    display: false,
                },
                ticks: {
                    font: {
                        family: "Inter, sans-serif",
                        size: 10
                    },
                    maxTicksLimit: 13
                }
            }
        }
    };

    const formatSeriesForDataSet = (useColor: number, label: string, data: (number | null)[]) => {
        return {
            label: label,
            data: data,
            borderColor: lineColors[useColor]['borderColor'],
            backgroundColor: lineColors[useColor]['backgroundColor'],
            showLine: false,
        }
    };

    const formatTrendlineDataSet = (useColor: number, label: string, data: (number | null)[]) => {
        return {
            label: `${label} (14d avg)`,
            data: data,
            borderColor: lineColors[useColor]['borderColor'],
            backgroundColor: 'transparent',
            showLine: true,
            spanGaps: true,
            pointRadius: 0,
            borderWidth: 2,
            borderDash: [],
        }
    };

    const computeRollingAverage = (history: nFitHistoryRecord[], dateStrings: string[]): (number | null)[] => {
        return dateStrings.map(dateStr => {
            const current = new Date(dateStr + 'T12:00:00');
            const windowStart = new Date(current);
            windowStart.setDate(windowStart.getDate() - 14);

            const inWindow = history.filter(r => {
                const d = new Date(r.date.toISOString().split('T')[0] + 'T12:00:00');
                return d >= windowStart && d <= current;
            });

            if (inWindow.length === 0) return null;
            return parseFloat((inWindow.reduce((sum, r) => sum + r.nFit, 0) / inWindow.length).toPrecision(4));
        });
    };
    
    useEffect(() => {
        const fetchData = async () => {
            if (coachedUsernames.length === 0) return;

            let mapData: StructuredData = {
                labels: [],
                datasets: []
            };

            const coachMemberHistory = await getCoachMemberHistory(coachedUsernames, start);

            let useColor = 0;
            let uniqueDateStrings: string[] = [];
            let dataToChart: DataToChart[] = [];
            coachMembers.forEach(member => {
                const history = coachMemberHistory.members.find(history => history.username === member.username, []);

                const member_with_history = {
                    id: member.id,
                    firstName: member.firstName,
                    lastName: member.lastName,
                    username: member.username,
                    history: history ? history.history.map(item => {
                        const objHistory: SerializableMemberHistory = {
                            id: item.id,
                            date: convertDateForHistory(item.date),
                            nFit: parseFloat(item.nFit.toPrecision(4)),
                            coachAnnotation: item.annotation,
                        };
                        return objHistory
                    }) : [],
                };

                // put this in the redux store
                dispatch(replaceMemberHistory(member_with_history));

                if (history && history.history.length > 0) {
                    const memberDateStrings = history.history.map(record => record.date.toISOString().split('T')[0]);
                    const allDates = new Set([...uniqueDateStrings, ...memberDateStrings]);
                    uniqueDateStrings = Array.from(allDates).sort();
                    const newData: DataToChart = {
                        label: member.firstName,
                        colorIndex: useColor,
                        history: history.history,
                    };
                    dataToChart.push(newData);
                }

                useColor++;
                if (useColor > lineColors.length - 1)
                    useColor = 0;
            });

            // inject 1st-of-month dates across the full range so every month gets a label
            if (uniqueDateStrings.length > 0) {
                const minDate = new Date(uniqueDateStrings[0] + 'T12:00:00');
                const maxDate = new Date(uniqueDateStrings[uniqueDateStrings.length - 1] + 'T12:00:00');
                const cursor = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
                if (cursor < minDate) cursor.setMonth(cursor.getMonth() + 1);
                while (cursor <= maxDate) {
                    const iso = cursor.toISOString().split('T')[0];
                    if (!uniqueDateStrings.includes(iso))
                        uniqueDateStrings.push(iso);
                    cursor.setMonth(cursor.getMonth() + 1);
                }
                uniqueDateStrings.sort();
            }

            // loop through dataToChart, format each series as a dataset, append
            const newMapData = dataToChart.flatMap(data => {
                const spacedData = uniqueDateStrings.map(dateStr => {
                    const hasRecord = data.history.find(record => record.date.toISOString().split('T')[0] === dateStr);
                    return hasRecord ? hasRecord.nFit : null;
                });

                const trendData = computeRollingAverage(data.history, uniqueDateStrings);

                return [
                    formatSeriesForDataSet(data.colorIndex, data.label, spacedData),
                    formatTrendlineDataSet(data.colorIndex, data.label, trendData),
                ];
            });
            mapData.labels = uniqueDateStrings.map(dateStr => {
                const d = new Date(dateStr + 'T12:00:00');
                if (d.getDate() === 1)
                    return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                return '';
            });
            mapData.datasets = newMapData;

            setData(mapData);
        }

        // finally make the call executing the above async code
        fetchData();
        
    }, [dispatch, start, coachedUsernames, coachMembers]);

    return (
        <Line
            options={options}
            data={data}
            plugins={[StripedBgPlugin]}
        />
    )
}

export default NFitGroupHistoryChart;